import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, Clock, Loader2, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { waLink } from "@/lib/conecta";
import { curLabel, fmtDate, money } from "@/lib/finance";
import { Section } from "@/components/common/DetailFields";
import { SemDot } from "@/components/conecta/Bits";

const StepRow = ({ s, onChanged, tone }) => {
  const [busy, setBusy] = useState(false);
  const act = async (body, msg) => {
    setBusy(true);
    try { await api.post(`/opportunities/${s.id}/next-step`, body); toast.success(msg); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const phone = s.contact?.whatsapp || s.contact?.phone;
  return (
    <li data-testid="hoy-step-item" className="py-2.5 border-b c-line last:border-0 space-y-1.5">
      <div className="flex items-start gap-2">
        <SemDot value={s.semaphore} size="w-2.5 h-2.5 mt-1.5" />
        <div className="flex-1 min-w-0">
          <Link to={`/conecta/negocios/${s.id}`} className="text-sm font-semibold hover:underline">{s.title}</Link>
          <p className={`text-xs ${tone}`}>{s.next_step_text || "Sin texto"} · {fmtDate(s.next_step_date)}{s.contact ? ` · ${s.contact.name}` : ""}</p>
        </div>
      </div>
      <div className="flex gap-1.5 pl-4">
        <button data-testid="hoy-step-done-btn" disabled={busy} onClick={() => act({ action: "done" }, "Marcado como hecho")} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white flex items-center gap-1">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Hecho</button>
        <button data-testid="hoy-step-postpone-1-btn" disabled={busy} onClick={() => act({ action: "postpone", days: 1 }, "Pospuesto 1 día")} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg c-card">+1 día</button>
        <button data-testid="hoy-step-postpone-7-btn" disabled={busy} onClick={() => act({ action: "postpone", days: 7 }, "Pospuesto 1 semana")} className="text-xs font-semibold px-2.5 py-1.5 rounded-lg c-card">+1 semana</button>
        {phone && <a data-testid="hoy-step-whatsapp-btn" href={waLink(phone)} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold px-2.5 py-1.5 rounded-lg c-card flex items-center gap-1"><MessageCircle className="w-3 h-3 text-emerald-600" /> WhatsApp</a>}
      </div>
    </li>
  );
};

const GROUPS = [
  { key: "vencidos", label: "Vencidos", tone: "text-red-500 font-semibold" },
  { key: "hoy", label: "Hoy", tone: "c-accent font-semibold" },
  { key: "semana", label: "Esta semana", tone: "c-text-2" },
];

export const StepsSection = ({ steps, onChanged }) => {
  const total = GROUPS.reduce((n, g) => n + steps[g.key].length, 0);
  return (
    <Section title="Próximos pasos" testid="hoy-steps">
      {total === 0 && <p className="text-sm c-muted">Nada pendiente esta semana.</p>}
      {GROUPS.map((g) => steps[g.key].length > 0 && (
        <div key={g.key} data-testid={`hoy-steps-${g.key}`} className="mt-1">
          <p className={`text-[11px] uppercase tracking-wider ${g.tone}`}>{g.key === "vencidos" && <AlertTriangle className="inline w-3 h-3 mr-1" />}{g.label} ({steps[g.key].length})</p>
          <ul>{steps[g.key].map((s) => <StepRow key={s.id} s={s} tone={g.tone} onChanged={onChanged} />)}</ul>
        </div>
      ))}
    </Section>
  );
};

export const StalledSection = ({ items, days }) => (
  <Section title={`Ya pasó tiempo · más de ${days} días`} testid="hoy-stalled">
    {items.length === 0 ? <p className="text-sm c-muted">Todos los negocios activos tienen contacto reciente.</p> : (
      <ul className="space-y-2">{items.map((s) => (
        <li key={s.id} data-testid="hoy-stalled-item" className="flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4 text-amber-500 shrink-0" />
          <Link to={`/conecta/negocios/${s.id}`} className="flex-1 hover:underline">{s.title}</Link>
          <span className="font-mono text-xs c-muted">{s.days_without_contact} d</span>
        </li>
      ))}</ul>
    )}
  </Section>
);

const AgreementRow = ({ r }) => (
  <li>
    <Link to={`/conecta/negocios/${r.opportunity_id}`} data-testid="agreement-row" className="block py-2.5 border-b c-line space-y-0.5 active:opacity-70">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold">{r.opportunity_title}</p>
        {r.change && <span data-testid="agreement-new-badge" className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#bc9538] text-[#0b2a47]">Nuevo</span>}
      </div>
      {r.change && <p className="text-[11px] c-accent font-semibold">{r.change}</p>}
      <p className="text-xs c-text-2">{r.payer ? `Paga: ${r.payer}` : r.counterpart ? `Contraparte: ${r.counterpart}` : "Quién paga: sin definir"} · {r.modality_text}</p>
      <p className="text-xs c-text-2">{r.status_label} · Por escrito: {r.confirmed_in_writing ? "sí" : "no"} · {fmtDate(r.agreement_date)}</p>
      <p data-testid="agreement-amounts" className="text-xs font-mono">
        {r.amounts_hidden ? "Montos ocultos" : `Esperado ${r.expected_amount == null ? "por definir" : money(r.expected_amount, r.currency)} · Cobrado ${money(r.collected_amount, r.currency)}`}
      </p>
    </Link>
  </li>
);

export const AgreementsSection = ({ a }) => (
  <Section title="Acuerdos" testid="hoy-agreements" action={<Link to="/conecta/comisiones" data-testid="hoy-agreements-all-link" className="text-xs c-accent font-semibold">Ver todos</Link>}>
    {a.new.length > 0 && (
      <div data-testid="hoy-agreements-new" className="mb-2">
        <p className="text-[11px] uppercase tracking-wider c-accent font-semibold">Nuevos ({a.new.length})</p>
        <ul>{a.new.map((r) => <AgreementRow key={r.id} r={r} />)}</ul>
      </div>
    )}
    <div data-testid="hoy-agreements-active">
      <p className="text-[11px] uppercase tracking-wider c-muted">Activos ({a.active_count})</p>
      {a.active.length === 0 ? <p className="text-sm c-muted py-1">Sin acuerdos activos.</p> : <ul>{a.active.map((r) => <AgreementRow key={r.id} r={r} />)}</ul>}
    </div>
    {a.por_cobrar_totals && Object.keys(a.por_cobrar_totals).length > 0 && (
      <p data-testid="hoy-agreements-totals" className="text-[11px] c-muted mt-2">
        Por cobrar: {Object.entries(a.por_cobrar_totals).map(([c, v]) => money(v, curLabel(c) === "Sin moneda" ? null : c)).join(" · ")}
      </p>
    )}
  </Section>
);

export const AiSummaryCard = () => {
  const [text, setText] = useState(null);
  const [busy, setBusy] = useState(false);
  const gen = async (refresh = false) => {
    setBusy(true);
    try { const { data } = await api.post("/hoy/ai-summary", null, { params: { refresh } }); setText(data.text); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Section title="Resumen del día (IA)" testid="hoy-ai-summary"
      action={text && <button data-testid="hoy-ai-refresh-btn" onClick={() => gen(true)} className="text-xs c-accent font-semibold">Actualizar</button>}>
      {text ? <p data-testid="hoy-ai-summary-text" className="text-sm leading-relaxed">{text}</p> : (
        <button data-testid="hoy-ai-generate-btn" disabled={busy} onClick={() => gen(false)} className="w-full py-2.5 rounded-xl c-card text-sm font-semibold flex items-center justify-center gap-1.5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 c-accent" />} Generar resumen
        </button>
      )}
    </Section>
  );
};
